import axios from "axios";

// Shared Allsale backend (auth, listings, chat, uploads). When
// REACT_APP_CLASSIFIEDS_API is set (preview) we hit the shared backend
// directly. When it's absent (prod build without the secret) we fall
// back to relative `/api` on the current origin — our FastAPI SSR helper
// exposes a catch-all `/api/{path}` proxy that forwards to the shared
// backend (iter-226 hotfix v2). Either path just works.
const SHARED = process.env.REACT_APP_CLASSIFIEDS_API;
const LOCAL = process.env.REACT_APP_BACKEND_URL;

// Iter-307 — runtime host guard. The direct shared-backend URL is a
// PREVIEW-ONLY convenience; deploy builds snapshot the pod filesystem so
// the var can leak into a production bundle. On any non-preview host
// (e.g. demoallsale.com) we ALWAYS use the same-origin `/api` proxy —
// otherwise production browsers would read/write the PREVIEW database
// (root cause of the Google sign-in "User not found" bug: the callback
// mints the user in the prod DB, then /auth/me validated against the
// preview DB).
// WO 806 — no platform hostnames in the bundle: a "preview" host is
// anything that is NOT one of our public production domains.
const PROD_HOST_RE = /(^|\.)allsale\.co\.nz$|(^|\.)demoallsale\.com$/;
const IS_PREVIEW_HOST =
  typeof window !== "undefined" && !PROD_HOST_RE.test(window.location.hostname);

/** Absolute origin of the shared backend on preview; "" (same-origin) everywhere else. */
export const SHARED_ORIGIN = IS_PREVIEW_HOST && SHARED ? SHARED : "";

const SHARED_BASE = SHARED_ORIGIN ? `${SHARED_ORIGIN}/api` : "/api";

// Shared Allsale backend (auth, listings, chat, uploads)
export const sharedApi = axios.create({
  baseURL: SHARED_BASE,
  timeout: 20000,
});

// WO 801c — stable per-browser device id, sent as X-Device-Id on signup /
// social-login requests so the backend can apply the welcome-bonus device
// limit. Random, persisted, never derived from anything personal.
export function getDeviceId() {
  try {
    let id = localStorage.getItem("allsale_device_id");
    if (!id) {
      id =
        (window.crypto?.randomUUID && window.crypto.randomUUID()) ||
        `dev_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
      localStorage.setItem("allsale_device_id", id);
    }
    return id;
  } catch {
    return "dev_unavailable";
  }
}

export const DEVICE_ID_HEADERS = () => ({ headers: { "X-Device-Id": getDeviceId() } });

// Local FastAPI helper (AI, sitemap)
export const localApi = axios.create({
  baseURL: LOCAL ? `${LOCAL}/api` : "/api",
  timeout: 30000,
});

// Attach bearer token on every request if we have one.
// Guard against the literal string "undefined"/"null" so we never send `Bearer undefined`.
sharedApi.interceptors.request.use((config) => {
  const token = localStorage.getItem("allsale_token");
  if (token && token !== "undefined" && token !== "null") {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Iter-412 §3 — multi-store: scope every /vendors/me* call to the
  // active store. Omitted header = oldest store (backend default).
  if ((config.url || "").includes("/vendors/me")) {
    const storeId = localStorage.getItem("allsale_active_store");
    if (storeId && storeId !== "undefined" && storeId !== "null") {
      config.headers["X-Store-Id"] = storeId;
    }
  }
  return config;
});

sharedApi.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      // Iter-290 §3 — session expiry UX. On any authed 401, clear tokens
      // and hard-redirect to /login preserving the return path. Skip
      // the redirect for /auth/* endpoints (login itself 401s on bad
      // credentials — those callers already show inline errors).
      const url = err?.config?.url || "";
      const isAuthCall = /^(?:\/)?(?:auth\/(?:login|register|forgot|reset))/i.test(url);
      // Iter 696 (694) — callers that render their own sign-in state
      // (engage claim) opt out of the hard redirect.
      const skipRedirect = err?.config?.skipAuthRedirect === true;
      localStorage.removeItem("allsale_token");
      localStorage.removeItem("allsale_user");
      if (!isAuthCall && !skipRedirect && typeof window !== "undefined") {
        const path = window.location.pathname + window.location.search;
        const alreadyOnLogin = /^\/login\b/.test(window.location.pathname);
        if (!alreadyOnLogin) {
          // Fire-and-forget — toast is best-effort; redirect is what
          // matters. We import lazily to avoid a circular init cycle.
          import("sonner").then(({ toast }) => {
            toast.error("Your session expired — please sign in again");
          }).catch(() => {});
          window.location.href = `/login?next=${encodeURIComponent(path)}`;
        }
      }
    }
    // Iter 755 — global phone gate. The backend returns HTTP 428
    // `{detail:{code:"phone_required", …}}` on write actions for accounts
    // with no mobile on file (Google SSO sign-ups, legacy imports).
    // Route them to the Add phone screen, preserving the return path.
    if (
      err?.response?.status === 428 &&
      err?.response?.data?.detail?.code === "phone_required" &&
      typeof window !== "undefined" &&
      !/^\/account\/add-phone\b/.test(window.location.pathname)
    ) {
      const path = window.location.pathname + window.location.search;
      import("sonner").then(({ toast }) => {
        toast.info(err.response.data.detail.message || "Add your mobile number to continue");
      }).catch(() => {});
      window.location.href = `/account/add-phone?next=${encodeURIComponent(path)}`;
    }
    // Normalise FastAPI 422 validation errors: `detail` can be a list of
    // `{loc, msg, type}` objects. React/toast can't render those, so flatten
    // to a human string BEFORE the error reaches components.
    //
    // Iter-290 fix: preserve field paths on "required/missing" items so
    // callers can show "Missing: nzbn, phone" instead of "Field required"
    // repeated 6 times.
    //
    // IMPORTANT: only flatten when the detail is a bare array or an object
    // with NO structured `code` field. Components that branch on
    // `detail.code` (e.g. car-valuation 402 payment_required) must still see
    // the machine-readable object.
    const detail = err?.response?.data?.detail;
    if (Array.isArray(detail)) {
      const missing = detail
        .filter((d) => d && typeof d === "object" && /required|missing/i.test(d.type || d.msg || ""))
        .map((d) => (Array.isArray(d.loc) ? d.loc.filter((p) => p !== "body").join(".") : d.msg))
        .filter(Boolean);
      if (missing.length) {
        err.response.data.detail = `Missing: ${missing.join(", ")}`;
      } else {
        err.response.data.detail = detail
          .map((d) => (d && typeof d === "object" ? d.msg || d.message : String(d)))
          .filter(Boolean)
          .join(", ");
      }
    } else if (detail && typeof detail === "object" && !detail.code) {
      err.response.data.detail = detail.msg || detail.message || JSON.stringify(detail);
    }
    return Promise.reject(err);
  },
);
